import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { css, Label, Modal, Toast, Spinner } from './UI';
import { Icono } from './Iconos';
import { CatalogoMotivosRechazo } from './MotivoRechazo';

// Administración › Aprobación de requisiciones.
//  · Validación de Finanzas: lista de validadores (basta con que apruebe uno).
//    Va al final del flujo: con las órdenes de compra emitidas, antes del pago.
//  · Aprobación del área: reglas por usuario, centro de costos o dirección (con
//    empresa opcional), en ese orden de prioridad. Cada regla define si aprueban
//    todos en orden o si basta con uno.
// La cadena se congela en cada requisición al crearla: cambiar una regla aquí
// no mueve las que ya van en camino.

const TIPOS = [
  { id: 'usuario', label: 'Un usuario', ayuda: 'Lo que pida esa persona (gana sobre las reglas de su área)' },
  { id: 'centro', label: 'Centro de costos', ayuda: 'Requisiciones de ese centro de costos' },
  { id: 'direccion', label: 'Dirección', ayuda: 'Requisiciones de esa dirección' },
];
const VACIA = { tipo: 'centro', usuarioSolicitante: '', centroCostos: '', direccion: '', empresa: '', modo: 'secuencial', aprobadores: [], nota: '', activa: true };

const aplicaA = (r) => {
  const emp = r.empresa ? ` · ${r.empresa}` : '';
  if (r.tipo === 'usuario') return `${r.usuarioSolicitante?.nombre || 'Usuario'}${emp}`;
  if (r.tipo === 'centro') return `Centro ${r.centroCostos}${emp}`;
  return `Dirección ${r.direccion}${emp}`;
};

function SelectorAprobadores({ usuarios, valor, onChange, ordenados }) {
  const [agregar, setAgregar] = useState('');
  const porId = useMemo(() => new Map(usuarios.map(u => [u._id, u])), [usuarios]);
  const mover = (i, d) => { const v = [...valor]; [v[i], v[i + d]] = [v[i + d], v[i]]; onChange(v); };
  return (
    <div>
      <ol style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 13 }}>
        {valor.map((id, i) => (
          <li key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
            <span style={{ flex: 1 }}>{porId.get(id)?.nombre || 'Usuario'} <span style={{ color: 'var(--glass)' }}>· {porId.get(id)?.email}</span></span>
            {ordenados && <>
              <button type="button" className="nx-ghost" aria-label="Subir" disabled={i === 0} onClick={() => mover(i, -1)} style={{ width: 28, height: 26 }}>↑</button>
              <button type="button" className="nx-ghost" aria-label="Bajar" disabled={i === valor.length - 1} onClick={() => mover(i, 1)} style={{ width: 28, height: 26 }}>↓</button>
            </>}
            <button type="button" className="nx-ghost" aria-label={`Quitar a ${porId.get(id)?.nombre}`} onClick={() => onChange(valor.filter(x => x !== id))} title="Quitar" style={{ width: 28, height: 26 }}><Icono nombre="cerrar" size={13} /></button>
          </li>
        ))}
        {!valor.length && <li style={{ listStyle: 'none', marginLeft: -20, color: 'var(--glass)' }}>Sin aprobadores todavía.</li>}
      </ol>
      <div style={{ display: 'flex', gap: 8 }}>
        <select value={agregar} onChange={e => setAgregar(e.target.value)} style={css.select(false)} aria-label="Agregar aprobador">
          <option value="">Agregar aprobador…</option>
          {usuarios.filter(u => !valor.includes(u._id)).map(u => <option key={u._id} value={u._id}>{u.nombre} · {u.email}</option>)}
        </select>
        <button type="button" disabled={!agregar} onClick={() => { onChange([...valor, agregar]); setAgregar(''); }} style={{ ...css.btnGhost, whiteSpace: 'nowrap' }}>Agregar</button>
      </div>
    </div>
  );
}

export default function ReglasAprobacion() {
  const [reglas, setReglas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [centros, setCentros] = useState([]);
  const [direcciones, setDirecciones] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [sinRegla, setSinRegla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { id?, form }
  const [finanzas, setFinanzas] = useState(null); // { id?, aprobadores }
  const [previa, setPrevia] = useState({ usuario: '', empresa: '', centroCostos: '', direccion: '', resultado: null });
  const [toast, setToast] = useState(null);
  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, us, cs, ds, es, sr] = await Promise.all([
        api.get('/reglas-aprobacion'),
        api.get('/auth/usuarios?limit=2000'),
        api.get('/centros/all?limit=2000').catch(() => ({ items: [] })),
        api.get('/centros/direcciones').catch(() => []),
        api.get('/empresas').catch(() => []),
        api.get('/reglas-aprobacion/sin-aprobador').catch(() => null),
      ]);
      setReglas(rs);
      setUsuarios((us.items || []).filter(u => u.activo).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
      setCentros([...new Set((cs.items || []).map(c => c.nombre))].sort());
      setDirecciones(ds || []);
      setEmpresas((es || []).map(e => e.nombre).sort());
      setSinRegla(sr);
      const rf = rs.find(r => r.tipo === 'finanzas');
      setFinanzas({ id: rf?._id, aprobadores: (rf?.aprobadores || []).map(a => a._id) });
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guardarFinanzas = async () => {
    try {
      if (!finanzas.aprobadores.length && finanzas.id) {
        if (!window.confirm('Sin validadores, la validación de Finanzas deja de aplicar a las requisiciones nuevas. ¿Continuar?')) return;
        await api.del(`/reglas-aprobacion/${finanzas.id}`);
      } else if (finanzas.aprobadores.length) {
        const body = { tipo: 'finanzas', aprobadores: finanzas.aprobadores };
        if (finanzas.id) await api.put(`/reglas-aprobacion/${finanzas.id}`, body); else await api.post('/reglas-aprobacion', body);
      }
      toast_('Validadores de Finanzas guardados'); load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const abrir = (r) => setModal(r
    ? { id: r._id, form: { ...VACIA, ...r, usuarioSolicitante: r.usuarioSolicitante?._id || '', aprobadores: (r.aprobadores || []).map(a => a._id) } }
    : { form: { ...VACIA } });
  const set = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));
  const guardar = async () => {
    try {
      if (modal.id) await api.put(`/reglas-aprobacion/${modal.id}`, modal.form); else await api.post('/reglas-aprobacion', modal.form);
      toast_('Regla guardada'); setModal(null); load();
    } catch (e) { toast_(e.message, 'error'); }
  };
  const borrar = async (r) => {
    if (!window.confirm(`¿Borrar la regla "${aplicaA(r)}"? Las requisiciones ya creadas conservan su cadena.`)) return;
    try { await api.del(`/reglas-aprobacion/${r._id}`); toast_('Regla borrada'); load(); } catch (e) { toast_(e.message, 'error'); }
  };
  const probar = async () => {
    if (!previa.usuario) { toast_('Elige un usuario', 'error'); return; }
    try {
      const qs = new URLSearchParams({ usuario: previa.usuario, empresa: previa.empresa, centroCostos: previa.centroCostos, direccion: previa.direccion });
      const resultado = await api.get(`/reglas-aprobacion/vista-previa?${qs}`);
      setPrevia(p => ({ ...p, resultado }));
    } catch (e) { toast_(e.message, 'error'); }
  };

  if (loading && !reglas.length) return <Spinner />;
  const delArea = reglas.filter(r => r.tipo !== 'finanzas');
  const h3 = { margin: '0 0 4px', fontFamily: 'var(--label-font)', fontSize: 16, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' };

  return (
    <div>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '0 0 16px', maxWidth: '80ch' }}>
        Flujo de una requisición: <strong>aprobadores del área</strong> → jefe de compras → cotización → autorización → órdenes de compra → <strong>validación de Finanzas</strong> → solicitud de pago.
        Nadie aprueba lo suyo. Cada requisición guarda su cadena al crearse, así que cambiar una regla no afecta a las que ya van en camino.
      </p>

      {/* ── Finanzas ── */}
      <div style={{ ...css.card, padding: '16px 20px', marginBottom: 18 }}>
        <h3 style={h3}>Validación de Finanzas</h3>
        <p style={{ fontSize: 12.5, color: 'var(--glass)', margin: '0 0 12px' }}>Valida la requisición completa cuando ya tiene sus órdenes de compra, antes de generar el pago. Basta con que apruebe uno. Sin validadores, esta etapa no aplica.</p>
        {finanzas && <SelectorAprobadores usuarios={usuarios} valor={finanzas.aprobadores} onChange={v => setFinanzas(f => ({ ...f, aprobadores: v }))} />}
        <div style={{ textAlign: 'right', marginTop: 10 }}><button type="button" onClick={guardarFinanzas} style={css.btn('primario')}>Guardar validadores</button></div>
      </div>

      {/* ── Área ── */}
      <div style={{ ...css.card, padding: 0, marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid var(--mesh-soft)' }}>
          <div>
            <h3 style={h3}>Aprobación del área</h3>
            <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>Prioridad: usuario, luego centro de costos, luego dirección. Con empresa gana sobre la que aplica a todas.</div>
          </div>
          <button type="button" onClick={() => abrir(null)} style={css.btn('secundario')}>Nueva regla</button>
        </div>
        {delArea.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Aplica a', 'Aprobadores', 'Cómo aprueban', 'Estado', ''].map((h, i) => <th key={i} scope="col" style={{ textAlign: 'left', padding: '9px 14px' }}>{h}</th>)}</tr></thead>
            <tbody>
              {delArea.map(r => (
                <tr key={r._id} style={{ borderTop: '1px solid var(--mesh-soft)', opacity: r.activa ? 1 : 0.55 }}>
                  <td style={{ padding: '9px 14px' }}>{aplicaA(r)}{r.nota && <div style={{ fontSize: 11.5, color: 'var(--glass)' }}>{r.nota}</div>}</td>
                  <td style={{ padding: '9px 14px' }}>{(r.aprobadores || []).map((a, i) => <div key={a._id}>{r.modo === 'secuencial' ? `${i + 1}. ` : ''}{a.nombre}{!a.activo && <span style={{ color: 'var(--danger)' }}> (inactivo)</span>}</div>)}</td>
                  <td style={{ padding: '9px 14px', color: 'var(--ink-soft)' }}>{r.modo === 'secuencial' ? 'Todos, en orden' : 'Basta con uno'}</td>
                  <td style={{ padding: '9px 14px' }}>{r.activa ? 'Activa' : 'Inactiva'}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => abrir(r)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px', marginRight: 6 }}>Editar</button>
                    <button type="button" onClick={() => borrar(r)} style={{ ...css.btnGhost, fontSize: 12, padding: '4px 10px' }}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div style={{ padding: 18, fontSize: 13, color: 'var(--glass)' }}>Sin reglas: las requisiciones se saltan la aprobación del área y van directo al jefe de compras.</div>}
      </div>

      {/* ── Sin regla ── */}
      {sinRegla?.sinRegla?.length > 0 && (
        <div style={{ ...css.card, padding: '14px 20px', marginBottom: 18, border: '1px solid var(--warn-line)' }}>
          <h3 style={h3}><span aria-hidden="true" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--st-pendiente)', marginRight: 8, verticalAlign: 'middle' }} />Solicitantes sin aprobador del área · {sinRegla.sinRegla.length} de {sinRegla.total}</h3>
          <p style={{ fontSize: 12.5, color: 'var(--glass)', margin: '0 0 8px' }}>{sinRegla.nota}</p>
          <div style={{ fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>{sinRegla.sinRegla.map(u => <span key={u._id}>{u.nombre}</span>)}</div>
        </div>
      )}

      {/* ── Vista previa ── */}
      <div style={{ ...css.card, padding: '16px 20px' }}>
        <h3 style={h3}>¿Quién aprobaría?</h3>
        <p style={{ fontSize: 12.5, color: 'var(--glass)', margin: '0 0 12px' }}>Simula la cadena de una requisición sin crearla.</p>
        <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) auto', gap: 10, alignItems: 'end' }}>
          <div><Label required>Solicitante</Label><select value={previa.usuario} onChange={e => setPrevia(p => ({ ...p, usuario: e.target.value, resultado: null }))} style={css.select(false)}><option value="">Elige…</option>{usuarios.map(u => <option key={u._id} value={u._id}>{u.nombre}</option>)}</select></div>
          <div><Label>Empresa</Label><select value={previa.empresa} onChange={e => setPrevia(p => ({ ...p, empresa: e.target.value, resultado: null }))} style={css.select(false)}><option value="">—</option>{empresas.map(x => <option key={x}>{x}</option>)}</select></div>
          <div><Label>Centro de costos</Label><select value={previa.centroCostos} onChange={e => setPrevia(p => ({ ...p, centroCostos: e.target.value, resultado: null }))} style={css.select(false)}><option value="">—</option>{centros.map(x => <option key={x}>{x}</option>)}</select></div>
          <div><Label>Dirección</Label><select value={previa.direccion} onChange={e => setPrevia(p => ({ ...p, direccion: e.target.value, resultado: null }))} style={css.select(false)}><option value="">—</option>{direcciones.map(x => <option key={x}>{x}</option>)}</select></div>
          <button type="button" onClick={probar} style={css.btnGhost}>Ver cadena</button>
        </div>
        {previa.resultado && (
          <div role="status" style={{ marginTop: 14, fontSize: 13, lineHeight: 1.6 }}>
            <div><strong>Área:</strong> {previa.resultado.area.aplica
              ? <>{previa.resultado.area.regla} — {previa.resultado.area.pasos.map((p, i) => `${i + 1}. ${p.join(' o ')}`).join(' → ')}</>
              : 'ninguna regla aplica: se salta esta etapa y va directo al jefe de compras.'}</div>
            <div><strong>Finanzas:</strong> {previa.resultado.finanzas.length ? `${previa.resultado.finanzas.join(' o ')} (al final, tras las órdenes de compra)` : 'sin validadores: no aplica.'}</div>
          </div>
        )}
      </div>

      {/* ── Motivos de rechazo ── */}
      <CatalogoMotivosRechazo avisar={toast_} />

      {/* ── Modal de regla ── */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.id ? 'Editar regla' : 'Nueva regla'} width={560}>
        {modal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <Label required>Aplica a</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TIPOS.map(t => (
                  <label key={t.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, padding: '6px 10px', border: `1px solid ${modal.form.tipo === t.id ? 'var(--plasma)' : 'var(--mesh)'}`, borderRadius: 4, cursor: 'pointer' }}>
                    <input type="radio" name="tipo" checked={modal.form.tipo === t.id} onChange={() => set('tipo', t.id)} /> {t.label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>{TIPOS.find(t => t.id === modal.form.tipo)?.ayuda}</div>
            </div>
            {modal.form.tipo === 'usuario' && <div><Label required>Usuario</Label><select value={modal.form.usuarioSolicitante} onChange={e => set('usuarioSolicitante', e.target.value)} style={css.select(false)}><option value="">Elige…</option>{usuarios.map(u => <option key={u._id} value={u._id}>{u.nombre} · {u.email}</option>)}</select></div>}
            {modal.form.tipo === 'centro' && <div><Label required>Centro de costos</Label><select value={modal.form.centroCostos} onChange={e => set('centroCostos', e.target.value)} style={css.select(false)}><option value="">Elige…</option>{centros.map(x => <option key={x}>{x}</option>)}</select></div>}
            {modal.form.tipo === 'direccion' && <div><Label required>Dirección</Label><select value={modal.form.direccion} onChange={e => set('direccion', e.target.value)} style={css.select(false)}><option value="">Elige…</option>{direcciones.map(x => <option key={x}>{x}</option>)}</select></div>}
            <div><Label>Empresa</Label><select value={modal.form.empresa} onChange={e => set('empresa', e.target.value)} style={css.select(false)}><option value="">Todas las empresas</option>{empresas.map(x => <option key={x}>{x}</option>)}</select></div>
            <div>
              <Label required>Aprobadores</Label>
              <SelectorAprobadores usuarios={usuarios} valor={modal.form.aprobadores} onChange={v => set('aprobadores', v)} ordenados={modal.form.modo === 'secuencial'} />
            </div>
            <div>
              <Label required>Cómo aprueban</Label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" name="modo" checked={modal.form.modo === 'secuencial'} onChange={() => set('modo', 'secuencial')} /> Todos, en el orden de la lista</label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" name="modo" checked={modal.form.modo === 'cualquiera'} onChange={() => set('modo', 'cualquiera')} /> Basta con uno</label>
              </div>
            </div>
            <div><Label>Nota</Label><input value={modal.form.nota} onChange={e => set('nota', e.target.value)} placeholder="Ej. cadena de Almacén" style={css.input(false)} /></div>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}><input type="checkbox" checked={modal.form.activa} onChange={e => set('activa', e.target.checked)} /> Regla activa</label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setModal(null)} style={css.btnGhost}>Cancelar</button>
              <button type="button" onClick={guardar} style={css.btn('primario')}>Guardar regla</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
